<template>
  <div class="dashboard-container">
    <!-- Welcome Section -->    <div class="welcome-section">
      <h1>{{ $t('raDashboard.welcome.title', { name: agentName }) }}</h1>
      <p class="subtitle">{{ $t('raDashboard.welcome.subtitle') }}</p>
    </div>

    <!-- Statistics Cards -->
    <div class="stats-grid">
      <div class="stat-card clickable" @click="goToBusiness">
        <div class="stat-icon"><i class="fas fa-briefcase"></i></div>
        <div class="stat-content">
          <h3>{{ $t('raDashboard.stats.totalBusinesses.title') }}</h3>
          <div class="stat-value">{{ totalBusinesses }}</div>
          <div class="stat-trend" :class="businessTrendClass">
            {{ businessTrend }}
          </div>
        </div>
      </div>

      <div class="stat-card clickable" @click="goToPendingMail">
        <div class="stat-icon"><i class="fas fa-inbox"></i></div>
        <div class="stat-content">
          <h3>{{ $t('raDashboard.stats.requestedMail.title') }}</h3>
          <div class="stat-value">{{ requestedMail }}</div>
          <div class="stat-trend" :class="mailTrendClass">
            {{ mailTrend }}
          </div>
        </div>
      </div>

      <div class="stat-card clickable" @click="goToCompletedMail">
        <div class="stat-icon"><i class="fas fa-check-circle"></i></div>
        <div class="stat-content">
          <h3>{{ $t('raDashboard.stats.completedMail.title') }}</h3>
          <div class="stat-value">{{ completedMail }}</div>
          <div class="stat-trend" :class="completedMailTrendClass">
            {{ completedMailTrend }}
          </div>
        </div>
      </div>

      <div class="stat-card clickable" @click="goToDocuments">
        <div class="stat-icon"><i class="fas fa-file-alt"></i></div>
        <div class="stat-content">
          <h3>{{ $t('raDashboard.stats.totalDocuments.title') }}</h3>
          <div class="stat-value">{{ totalDocuments }}</div>
          <div class="stat-trend" :class="documentsTrendClass">
            {{ documentsTrend }}
          </div>
        </div>
      </div>

    </div>

    <!-- Recent Activity -->
    <!-- <div class="recent-activity">
      <div class="section-header">
        <h2>Recent Activity</h2>
        <button class="view-all-btn" @click="viewAllActivity">View All</button>
      </div>
      <div class="activity-list">
        <div v-if="loading" class="loading-state">
          <i class="fas fa-spinner fa-spin"></i>
          Loading activities...
        </div>
        <div v-else-if="error" class="error-state">
          <i class="fas fa-exclamation-circle"></i>
          {{ error }}
          <button @click="fetchActivities" class="retry-btn">
            <i class="fas fa-redo"></i> Retry
          </button>
        </div>
        <div v-else-if="activities.length === 0" class="empty-state">
          No recent activities
        </div>
        <div v-else class="activity-items">
          <div v-for="activity in activities" :key="activity.id" class="activity-item">
            <div class="activity-icon" :class="activity.type">
              <i :class="getActivityIcon(activity.type)"></i>
            </div>
            <div class="activity-details">
              <h4>{{ activity.title }}</h4>
              <p>{{ activity.description }}</p>
              <span class="activity-time">{{ formatDate(activity.timestamp) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div> -->

    <!-- Activity Modal -->
    <div class="modal" v-if="showActivityModal">
      <div class="modal-content">
        <div class="modal-header">
          <h2>{{ $t('raDashboard.activity.allActivities') }}</h2>
          <button class="close-btn" @click="showActivityModal = false">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <div v-if="loadingAll" class="loading-state">
            <i class="fas fa-spinner fa-spin"></i>
            {{ $t('raDashboard.activity.loading') }}
          </div>
          <div v-else-if="error" class="error-state">
            <i class="fas fa-exclamation-circle"></i>
            {{ error }}
            <button @click="fetchAllActivities" class="retry-btn">
              <i class="fas fa-redo"></i> {{ $t('raDashboard.activity.retry') }}
            </button>
          </div>
          <div v-else-if="allActivities.length === 0" class="empty-state">
            {{ $t('raDashboard.activity.noActivities') }}
          </div>
          <div v-else class="activity-items">
            <div v-for="activity in allActivities" :key="activity.id" class="activity-item">
              <div class="activity-icon" :class="activity.type">
                <i :class="getActivityIcon(activity.type)"></i>
              </div>
              <div class="activity-details">
                <h4>{{ activity.title }}</h4>
                <p>{{ activity.description }}</p>
                <span class="activity-time">{{ formatDate(activity.timestamp) }}</span>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="close-btn" @click="showActivityModal = false">{{ $t('raDashboard.activity.close') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import '@/styles/RADashboard.css'
import businessService from '@/services/businessService'
import incomingMailService from '../services/incomingMailService'
import documentService from '@/services/documentService'
import activityService from '@/services/activityService'
import authService from '@/services/authService'
import { useI18n } from 'vue-i18n'

export default {
  name: 'RADashboard',
  props: {
    currentLanguage: {
      type: String,
      default: 'English'
    }
  },
  setup() {
    const { t } = useI18n()
    return { t }
  },
  data() {
    return {
      agentName: '',
      agentId: null,
      mailItems: [],
      totalBusinesses: 0,
      requestedMail: 0,
      completedMail: 0,
      totalDocuments: 0,
      businessTrend: '',
      mailTrend: '',
      completedMailTrend: '',
      documentsTrend: '',
      activities: [],
      allActivities: [],
      loading: false,
      loadingAll: false,
      error: null,
      showActivityModal: false,
      currentPage: 1,
      itemsPerPage: 20,
      requestedStatuses: ['Scan_requested', 'Forward_requested', 'Shred_requested']
    }
  },
  computed: {
    businessTrendClass() {
      return this.getTrendClass(this.businessTrend)
    },
    mailTrendClass() {
      return this.getTrendClass(this.mailTrend)
    },
    completedMailTrendClass() {
      return this.getTrendClass(this.completedMailTrend)
    },
    documentsTrendClass() {
      return this.getTrendClass(this.documentsTrend)
    },
    getRequestedCount() {
      return this.mailItems.filter(mail => this.requestedStatuses.includes(mail.status)).length
    },
    newBusinessesCount() {
      const match = this.businessTrend.match(/\+(\d+)/)
      return match ? match[1] : 0
    },
    newCompletedMailCount() {
      const match = this.completedMailTrend.match(/\+(\d+)/)
      return match ? match[1] : 0
    },
    newDocumentsCount() {
      const match = this.documentsTrend.match(/\+(\d+)/)
      return match ? match[1] : 0
    }
  },
  watch: {
    currentLanguage: {
      immediate: true,
      handler(newLanguage) {
        const lang = newLanguage === 'English' ? 'en' : 'es'
        this.$i18n.locale = lang
      }
    }
  },
  methods: {
    async fetchDashboardData() {
      this.loading = true
      this.error = null
      try {
        const agentId = localStorage.getItem('agentId');

        // Fetch businesses
        const businessResponse = await businessService.getAllBusinesses(agentId)
        if (businessResponse && businessResponse.data) {
          this.totalBusinesses = businessResponse.data.length
          const lastMonth = new Date()
          lastMonth.setMonth(lastMonth.getMonth() - 1)
          const newBusinesses = businessResponse.data.filter(b =>
            b.createdAt && new Date(b.createdAt) > lastMonth
          )
          this.businessTrend = newBusinesses.length > 0
            ? this.t('raDashboard.stats.totalBusinesses.new', { count: newBusinesses.length })
            : this.t('raDashboard.stats.totalBusinesses.noNew')
        }        // Fetch all mail
        const allMailResponse = await incomingMailService.getIncomingMail()
        if (allMailResponse && allMailResponse.data) {
          this.mailItems = allMailResponse.data
          
          // Count completed mails (Scanned, Forwarded, Shredded)
          this.completedMail = this.mailItems.filter(mail => 
            ['Scanned', 'Forwarded', 'Shredded'].includes(mail.status)
          ).length
          
          const lastMonthCompleted = new Date()
          lastMonthCompleted.setMonth(lastMonthCompleted.getMonth() - 1)
          const newCompletedMails = this.mailItems.filter(m => 
            ['Scanned', 'Forwarded', 'Shredded'].includes(m.status) && 
            m.lastActionDate && 
            new Date(m.lastActionDate) > lastMonthCompleted
          )
          
          // Use computed property for requested mail count
          this.requestedMail = this.getRequestedCount
          this.mailTrend = this.requestedMail > 0
            ? this.t('raDashboard.stats.requestedMail.pending', { count: this.requestedMail })
            : this.t('raDashboard.stats.requestedMail.noPending')
          this.completedMailTrend = newCompletedMails.length > 0
            ? this.t('raDashboard.stats.completedMail.new', { count: newCompletedMails.length })
            : this.t('raDashboard.stats.completedMail.noNew')
        }

        

        // Fetch documents
        const documentResponse = await documentService.getAllDocuments()
        if (documentResponse && documentResponse.data) {
          this.totalDocuments = documentResponse.data.length
          const lastMonth = new Date()
          lastMonth.setMonth(lastMonth.getMonth() - 1)
          const newDocuments = documentResponse.data.filter(d =>
            d.createdAt && new Date(d.createdAt) > lastMonth
          )
          this.documentsTrend = newDocuments.length > 0
            ? this.t('raDashboard.stats.totalDocuments.new', { count: newDocuments.length })
            : this.t('raDashboard.stats.totalDocuments.noNew')
        }

        // Fetch activities
        await this.fetchActivities()
      } catch (error) {
        console.error('Error fetching dashboard data:', error)
        this.error = this.t('raDashboard.errors.dashboardLoadFailed')
      } finally {
        this.loading = false
      }
    },
    async fetchActivities() {
      try {
        if (!this.agentId) return
        const response = await activityService.getAgentActivities(this.agentId)
        if (response && response.data) {
          this.activities = response.data
        }
      } catch (error) {
        console.error('Error fetching activities:', error)
        this.error = this.t('raDashboard.errors.activitiesLoadFailed')
      }
    },
    goToBusiness() {
      this.$router.push({ name: 'RABusiness' })
    },
    goToPendingMail() {
      this.$router.push({ name: 'RAMailRequest' })
    },
    goToCompletedMail() {
      this.$router.push({ name: 'RAMailRequest' })
    },
    goToDocuments() {
      this.$router.push({ name: 'RAIncomingMail' })
    },
    async fetchAllActivities() {
      this.loadingAll = true
      this.error = null
      try {
        if (!this.agentId) return
        const response = await activityService.getAllAgentActivities(
          this.agentId,
          this.currentPage,
          this.itemsPerPage
        )
        if (response && response.data) {
          this.allActivities = response.data
        }
      } catch (error) {
        console.error('Error fetching all activities:', error)
        this.error = this.t('raDashboard.errors.activitiesLoadFailed')
      } finally {
        this.loadingAll = false
      }
    },
    getTrendClass(trend) {
      if (trend.includes('+')) return 'positive'
      if (trend.includes('-')) return 'negative'
      return 'neutral'
    },
    getActivityIcon(type) {
      const icons = {
        mail: 'fas fa-envelope',
        document: 'fas fa-file-alt',
        business: 'fas fa-building',
        task: 'fas fa-tasks'
      }
      return icons[type] || 'fas fa-info-circle'
    },
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    async viewAllActivity() {
      this.showActivityModal = true
      await this.fetchAllActivities()
    }
  },
  created() {
    const agentData = JSON.parse(localStorage.getItem('agentData') || '{}')
    if (!agentData.firstName && !agentData.lastName) {
      // Try to get agent data from email
      const agentEmail = localStorage.getItem('agentEmail')
      if (agentEmail) {
        authService.getAgentByEmail(agentEmail)
          .then(response => {
            if (response.success && response.agent) {
              this.agentName = `${response.agent.firstName} ${response.agent.lastName}`.trim()
              // Store the data for future use
              localStorage.setItem('agentData', JSON.stringify(response.agent))
            }
          })
          .catch(error => {
            console.error('Error fetching agent data:', error)
            this.error = this.t('raDashboard.errors.loadFailed')
          })
      }
    } else {
      this.agentName = `${agentData.firstName} ${agentData.lastName}`.trim()
    }
    this.fetchDashboardData()
  }
}
</script>
